import { DEFAULT_THEME, THEMES, THEME_META, type ThemeName } from './theme-contract'

/** Ключ в localStorage. Вынесен, потому что его же читает инлайн-скрипт ниже. */
export const THEME_STORAGE_KEY = 'taro:theme'

export const isThemeName = (v: unknown): v is ThemeName =>
  typeof v === 'string' && (THEMES as readonly string[]).includes(v)

/**
 * Применить тему.
 *
 * Ставит один атрибут на <html> — этого достаточно, потому что тема
 * это только переменные. Ни один компонент не перерисовывается по-новому,
 * меняются значения, на которые он уже ссылается.
 */
export function applyTheme(theme: ThemeName, opts: { persist?: boolean } = {}): void {
  const { persist = true } = opts

  document.documentElement.dataset.theme = theme

  if (persist) {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme)
    } catch {
      // Приватный режим или переполненное хранилище. Тема применится
      // на эту сессию — это лучше, чем уронить приложение из-за настройки.
    }
  }
}

export function getStoredTheme(): ThemeName | null {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY)
    return isThemeName(v) ? v : null
  } catch {
    return null
  }
}

export function getCurrentTheme(): ThemeName {
  const v = document.documentElement.dataset.theme
  return isThemeName(v) ? v : DEFAULT_THEME
}

/**
 * Прочитать значение роли — чтобы отдать его наружу.
 *
 * Нужно ровно для одного: покрасить нативный хром Telegram в наш фон,
 * чтобы не было шва между приложением и хедером. Сам пакет про Telegram
 * не знает и знать не должен — вызывает @taro/tg.
 */
export function readToken(role: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(`--c-${role}`).trim()
}

/**
 * Инлайн-скрипт для <head>, до первой отрисовки.
 *
 * Без него между загрузкой HTML и запуском React успевает мигнуть тема
 * по умолчанию. На тёмном приложении вспышка светлой темы особенно заметна
 * и выглядит как поломка.
 *
 * Строкой, а не функцией: это должно выполниться синхронно в <head>,
 * когда бандла ещё нет.
 */
export const themeInitScript = `(function(){try{var t=localStorage.getItem(${JSON.stringify(
  THEME_STORAGE_KEY,
)});var ok=${JSON.stringify(THEMES)};if(ok.indexOf(t)>-1)document.documentElement.dataset.theme=t;}catch(e){}})()`

export { DEFAULT_THEME, THEMES, THEME_META }
export type { ThemeName }
export type { ThemeMeta, ThemeRole } from './theme-contract'
